import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight, X, Expand } from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { findPlant, plateImg, zoomImg, pad2, cleanTribe, PLANTS } from '@/lib/databank';

const FIELDS = [
    ['Tribe', (p) => cleanTribe(p.tribe)],
    ['Subtribe', (p) => p.subtribe],
    ['Synonyms', (p) => p.synonyms],
    ['Habit', (p) => p.habit],
    ['Habitat', (p) => p.habitat],
    ['Native Range', (p) => p.native],
    ['Occurrence', (p) => p.occurrence],
    ['Threatened Category', (p) => p.threatened_category],
    ['Key Characters', (p) => p.key_characters],
    ['Botanical Description', (p) => p.description],
    ['Flowering & Fruiting', (p) => p.flowering_fruiting],
    ['Distribution', (p) => p.distribution],
    ['Ecology', (p) => p.ecology],
    ['Economic Importance', (p) => p.economic_importance],
];

const FieldRow = ({ label, value }) => {
    if (!value || !value.trim()) return null;
    return (
        <div className="grid gap-2 border-t border-line py-5 md:grid-cols-[180px_1fr] md:gap-8" data-testid={`field-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
            <dt className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.25em] text-mute">{label}</dt>
            <dd className="text-sm leading-relaxed text-ink/85">{value}</dd>
        </div>
    );
};

export default function PlantDetail() {
    const { id } = useParams();
    const reduce = useReducedMotion();
    const plant = findPlant(id);
    const [lightbox, setLightbox] = useState(false);

    useEffect(() => { setLightbox(false); }, [id]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') setLightbox(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    useEffect(() => {
        if (window.__lenis) lightbox ? window.__lenis.stop() : window.__lenis.start();
    }, [lightbox]);

    if (!plant) {
        return (
            <div className="min-h-screen bg-paper text-ink" data-testid="plant-not-found">
                <Header />
                <main className="mx-auto max-w-[1500px] px-5 py-40 md:px-10">
                    <p className="font-serif text-4xl italic text-mute">No specimen under that plate number.</p>
                    <Link to="/gallery" data-testid="not-found-back-link" className="u-line mt-6 inline-block font-mono text-[11px] uppercase tracking-[0.25em] text-ink">
                        Return to the index
                    </Link>
                </main>
                <Footer />
            </div>
        );
    }

    const prev = PLANTS.find((p) => p.plant_no === plant.plant_no - 1);
    const next = PLANTS.find((p) => p.plant_no === plant.plant_no + 1);

    return (
        <div className="min-h-screen bg-paper text-ink" data-testid="plant-detail-page">
            <div className="noise-overlay" aria-hidden="true" />
            <Header />
            <main className="mx-auto max-w-[1500px] px-5 pb-24 md:px-10">
                <motion.div
                    key={plant.plant_no}
                    initial={reduce ? false : { opacity: 0, y: 28 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="grid gap-12 pt-12 md:pt-16 lg:grid-cols-2 lg:gap-16"
                >
                    {/* sticky plate */}
                    <div className="lg:sticky lg:top-24 lg:self-start">
                        <button
                            type="button"
                            onClick={() => setLightbox(true)}
                            data-testid="plate-lightbox-open"
                            className="group relative block w-full cursor-zoom-in border border-line bg-[#FDFCF6] p-6 md:p-10"
                        >
                            <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 75% 60% at 50% 28%, rgba(255,255,255,0.9), rgba(229,225,213,0.35) 78%)' }} />
                            <img src={plateImg(plant)} alt={plant.botanical_name} className="plate-img relative mx-auto w-full max-w-[480px]" />
                            <span className="absolute bottom-4 right-4 flex items-center gap-2 border border-line bg-paper px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.22em] text-ink opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                                <Expand className="h-3 w-3" /> Enlarge
                            </span>
                        </button>
                        <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-mute">
                            Plate {pad2(plant.plant_no)} — flowering twig & floral parts
                        </p>
                    </div>

                    {/* taxonomy record */}
                    <div>
                        <Link to="/gallery" data-testid="detail-back-link" className="u-line font-mono text-[11px] uppercase tracking-[0.25em] text-mute">
                            ← The Index
                        </Link>
                        <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.3em] text-terra" data-testid="detail-plate-no">
                            Specimen No. {pad2(plant.plant_no)}
                        </p>
                        <h1 className="mt-3 font-serif text-4xl italic leading-tight text-ink md:text-6xl" data-testid="detail-botanical-name">
                            {plant.botanical_name}
                        </h1>
                        <p className="mt-3 text-base text-mute" data-testid="detail-common-name">{plant.common_name.replace(/,$/, '')}</p>

                        <div className="mt-6 flex flex-wrap gap-2">
                            {plant.occurrence && (
                                <span className="border border-line px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.22em] text-ink" data-testid="detail-occurrence-badge">
                                    {plant.occurrence}
                                </span>
                            )}
                            {plant.threatened_category && (
                                <span className="border border-line px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.22em] text-ink" data-testid="detail-threatened-badge">
                                    {plant.threatened_category.replace(/\.$/, '')}
                                </span>
                            )}
                        </div>

                        <dl className="mt-10 border-b border-line pb-2">
                            {FIELDS.map(([label, get]) => (
                                <FieldRow key={label} label={label} value={get(plant)} />
                            ))}
                        </dl>

                        {/* prev / next */}
                        <div className="mt-10 flex items-center justify-between gap-4" data-testid="detail-prevnext">
                            {prev ? (
                                <Link to={`/plant/${prev.plant_no}`} data-testid="detail-prev-link" className="group flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink">
                                    <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
                                    <span className="hidden sm:inline font-serif normal-case italic tracking-normal text-base text-mute">{prev.botanical_name}</span>
                                    <span className="sm:hidden">Prev</span>
                                </Link>
                            ) : <span />}
                            {next ? (
                                <Link to={`/plant/${next.plant_no}`} data-testid="detail-next-link" className="group flex items-center gap-3 text-right font-mono text-[10px] uppercase tracking-[0.2em] text-ink">
                                    <span className="hidden sm:inline font-serif normal-case italic tracking-normal text-base text-mute">{next.botanical_name}</span>
                                    <span className="sm:hidden">Next</span>
                                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                </Link>
                            ) : <span />}
                        </div>
                    </div>
                </motion.div>
            </main>

            {/* lightbox */}
            <AnimatePresence>
                {lightbox && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/95 p-6 backdrop-blur-sm"
                        onClick={() => setLightbox(false)}
                        data-testid="plate-lightbox"
                    >
                        <button
                            type="button"
                            onClick={() => setLightbox(false)}
                            data-testid="lightbox-close-button"
                            className="absolute right-6 top-6 flex items-center gap-2 border border-paper/30 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-paper transition-colors duration-300 hover:bg-paper hover:text-ink"
                        >
                            Close <X className="h-4 w-4" />
                        </button>
                        <motion.img
                            initial={reduce ? false : { scale: 0.94 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                            src={zoomImg(plant)}
                            alt={`${plant.botanical_name} — detail`}
                            className="max-h-[85vh] max-w-full bg-paper object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />
                        <p className="absolute bottom-6 left-6 font-mono text-[10px] uppercase tracking-[0.3em] text-paper/60">
                            Plate {pad2(plant.plant_no)} · {plant.botanical_name}
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>

            <Footer />
        </div>
    );
}
